import React from "react"
import cx from "classnames"
import "./checkbox.css"

/**
 * The rewritten checkbox is the named export, reached on the path consumers
 * already import from:
 *
 *     import Checkbox from "@kit/design/checkbox"    // legacy, below
 *     import { Checkbox } from "@kit/design/checkbox" // rewritten
 *
 * A call site migrates by changing that one line and its props, and this file
 * disappears once none of them import the default any more.
 */
export { Checkbox } from "../checkbox-new"
export type { CheckboxProps } from "../checkbox-new"

type LegacyCheckboxColor = "green" | "blue" | "red" | "dark-gray"

/** @deprecated Import the named `Checkbox` instead. See the module comment. */
export type LegacyCheckboxProps = {
  /** Additional CSS class names */
  className?: string
  /** Inline styles applied to the checkbox */
  style?: React.CSSProperties
  /** Color theme for the checkbox */
  color?: LegacyCheckboxColor
  /** Displays a strike-through line over the checkbox, useful for partial/indeterminate states */
  strikeThrough?: boolean
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, "type" | "style">

const colorOverrides: Partial<Record<LegacyCheckboxColor, string>> = {
  blue: "var(--blue-500)",
  red: "var(--red-500)",
  "dark-gray": "var(--gray-800)"
}

/**
 * A styled checkbox input with color variants and an optional strike-through state
 * for representing partial or indeterminate selections.
 *
 * @deprecated Import the named `Checkbox` instead. See the module comment.
 */
function LegacyCheckbox({
  className,
  style,
  color,
  strikeThrough,
  ...props
}: LegacyCheckboxProps) {
  const classNames = cx(
    className,
    "design-custom-box",
    "design-checkbox",
    "text-base",
    "flex-shrink-0",
    "shrink-0"
  )
  const styleOverride =
    color && color !== "green"
      ? {
          "--active-checkbox": colorOverrides[color]
        }
      : null

  if (strikeThrough) {
    return (
      <div className="relative inline-block size-5">
        <input
          className={classNames}
          style={
            {
              ...styleOverride,
              ...style,
              "--border": color ? colorOverrides[color] : undefined,
              borderWidth: "2px"
            } as React.CSSProperties
          }
          type="checkbox"
          {...props}
        />
        <span
          className="absolute checkbox-strike-through"
          style={{
            backgroundColor: color ? colorOverrides[color] : undefined
          }}
        />
      </div>
    )
  }

  return (
    <input
      className={classNames}
      style={{ ...styleOverride, ...style } as React.CSSProperties}
      type="checkbox"
      {...props}
    />
  )
}

export default LegacyCheckbox
